/** Typed bindings for feature-owned table filters and supported sort columns. */
import { type ColumnDef } from "@tanstack/react-table";

import { type TableFacetOption } from "@/shared/components/table-facet";
import {
  type TableFilterDefinition,
  type TableFilterValue,
} from "@/shared/components/table-toolbar";

/** Base callbacks shared by each field kind; feature code supplies its semantics. */
export type TableFilterBinding = Pick<
  TableFilterDefinition,
  "id" | "label" | "value" | "onChange" | "onRemove"
>;

/** Bind a feature's filter object without moving API mappings into the toolbar. */
export function bindTableFilters<T extends { [Key in keyof T]: TableFilterValue }>(
  values: T,
  update: (patch: Partial<T>) => void,
): (key: Extract<keyof T, string>, label: string) => TableFilterBinding {
  return (key, label) => ({
    id: key,
    label,
    value: values[key],
    onChange: (value) => update({ [key]: value } as Partial<T>),
    onRemove: () => update({ [key]: undefined } as Partial<T>),
  });
}

/** Use only explicitly server-sortable string-labelled column definitions. */
export function tableSortFields<T>(columns: ColumnDef<T>[]): TableFacetOption[] {
  return columns.flatMap((column) =>
    column.enableSorting && column.id && typeof column.header === "string"
      ? [{ value: column.id, label: column.header }]
      : [],
  );
}
